import React, { useState, useEffect } from 'react';
import { BarChart3, DollarSign, TrendingUp, PiggyBank, ChevronDown, AlertCircle, FileText, UserCheck } from 'lucide-react';
import PerformanceTab from './PerformanceTab';
import FinancialTab from './FinancialTab';
import VisualTab from './VisualTab';
import BookPortfolioTab from './BookPortfolioTab';
import ClosedAnalysisTab from './ClosedAnalysisTab';
import MemberAnalysisTab from './MemberAnalysisTab';
import SystemReportsTab from './SystemReportsTab';
import OfficerAnalysisTab from './OfficerAnalysisTab';
import { metricsAuthUtils } from './api';
import { useAuth } from '../Auth/AuthContext';

const MetricsDashboard = ({ isDark = false }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [showClosedAnalysis, setShowClosedAnalysis] = useState(false);
  const [showMemberAnalysis, setShowMemberAnalysis] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [customDateRange, setCustomDateRange] = useState({ startDate: '', endDate: '' });
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authError, setAuthError] = useState(null);
  
  const { user } = useAuth();

  useEffect(() => {
    checkAuthentication();
  }, []);

  const checkAuthentication = () => {
    try {
      const authenticated = metricsAuthUtils.isAuthenticated();
      setIsAuthenticated(authenticated);
      
      if (!authenticated) {
        setAuthError('You must be logged in to view metrics');
      }
    } catch (error) {
      console.error('Authentication check failed:', error);
      setAuthError('Authentication error occurred');
    }
  };

  const handlePeriodChange = (period, dateRange = null) => {
    console.log('Period changed:', period, dateRange);
    setSelectedPeriod(period);
    if (dateRange && period === 'custom') {
      setCustomDateRange(dateRange);
    } else if (period !== 'custom') {
      setCustomDateRange({ startDate: '', endDate: '' });
    }
  };

  const handleViewMemberDetails = (group) => {
    setSelectedGroup({
      ...group,
      selectedPeriod: selectedPeriod,
      customDateRange: selectedPeriod === 'custom' ? customDateRange : null
    });
    setShowMemberAnalysis(true);
  };

  const handleBackFromMembers = () => {
    setShowMemberAnalysis(false);
    setSelectedGroup(null);
  };

  const handleBackFromClosedAnalysis = () => {
    setShowClosedAnalysis(false);
    setShowMemberAnalysis(false);
    setSelectedGroup(null);
  };

  // Helper function to check if user has access to a tab
  const hasAccess = (tab) => {
    if (!tab.requiresUsername) return true;
    
    if (Array.isArray(tab.requiresUsername)) {
      return tab.requiresUsername.includes(user?.username);
    }
    
    return user?.username === tab.requiresUsername;
  };

  // Filter tabs based on username
  const allTabs = [
    { label: 'Performance Analytics', subtitle: 'Client & group analytics', icon: BarChart3 },
    { label: 'Financial Accounting', subtitle: 'Financial data', icon: DollarSign, requiresUsername: ['Mary', 'Obah'] },
    { label: 'Visual Analytics', subtitle: 'Charts & graphs', icon: TrendingUp },
    { label: 'Book Portfolio', subtitle: 'Group portfolio overview', icon: PiggyBank },
    { label: 'Officer Analysis', subtitle: 'Officer performance metrics', icon: UserCheck },
    { label: 'System Reports', subtitle: 'Meeting & group reports', icon: FileText }
  ];
  
  const tabs = allTabs.filter(tab => hasAccess(tab));

  // Show authentication error if not authenticated
  if (!isAuthenticated) {
    return (
      <div className={`w-full min-h-screen flex items-center justify-center ${isDark ? 'bg-[#1f262e]' : 'bg-gray-50'}`}>
        <div className={`max-w-md w-full rounded-lg p-8 ${isDark ? 'bg-[#2b323c] border border-gray-700' : 'bg-white border border-gray-200'}`}>
          <div className="flex items-center gap-3 mb-4">
            <AlertCircle className="w-6 h-6 text-red-500" />
            <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Authentication Required
            </h2>
          </div>
          <p className={`mb-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {authError || 'You must be logged in to access the metrics dashboard.'}
          </p>
          <button
            onClick={() => window.location.href = '/login'}
            className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  // Map the filtered tab index to the original tab index for content rendering
  const getOriginalTabIndex = (filteredIndex) => {
    const filteredTab = tabs[filteredIndex];
    return allTabs.findIndex(tab => tab.label === filteredTab.label);
  };

  const originalActiveTabIndex = getOriginalTabIndex(activeTab);

  return (
    <div className={`w-full min-h-screen ${isDark ? 'bg-[#1f262e]' : 'bg-gray-50'}`}>
      {/* Tabs */}
      <div className={`border-b px-6 py-4 ${isDark ? 'bg-[#2b323c] border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="flex gap-2 overflow-x-auto">
          {tabs.map((tab, index) => {
            const Icon = tab.icon;
            return (
              <button
                key={index}
                onClick={() => {
                  setActiveTab(index);
                  setShowClosedAnalysis(false);
                  setShowMemberAnalysis(false);
                }}
                className={`px-6 py-3 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                  activeTab === index
                    ? 'bg-blue-600 text-white'
                    : isDark 
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4" />
                  <div className="text-left">
                    <div>{tab.label}</div>
                    <div className="text-xs opacity-75">{tab.subtitle}</div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="p-6 pb-24">
        {showMemberAnalysis ? (
          <MemberAnalysisTab 
            groupData={selectedGroup}
            isDark={isDark}
            onBack={handleBackFromMembers}
          />
        ) : showClosedAnalysis ? (
          <ClosedAnalysisTab 
            isDark={isDark} 
            selectedPeriod={selectedPeriod}
            customDateRange={customDateRange}
            onViewMemberDetails={handleViewMemberDetails}
            onBack={handleBackFromClosedAnalysis}
          />
        ) : (
          <>
            {originalActiveTabIndex === 0 && (
              <PerformanceTab 
                isDark={isDark} 
                selectedPeriod={selectedPeriod}
              />
            )}
            {originalActiveTabIndex === 1 && hasAccess(allTabs[1]) && (
              <FinancialTab 
                isDark={isDark} 
                onExplore={() => setShowClosedAnalysis(true)}
                onPeriodChange={handlePeriodChange}
              />
            )}
            {originalActiveTabIndex === 2 && (
              <VisualTab 
                isDark={isDark}
                selectedPeriod={selectedPeriod}
              />
            )}
            {originalActiveTabIndex === 3 && (
              <BookPortfolioTab 
                isDark={isDark}
                selectedPeriod={selectedPeriod}
              />
            )}
            {originalActiveTabIndex === 4 && (
              <OfficerAnalysisTab 
                isDark={isDark}
                selectedPeriod={selectedPeriod}
              />
            )}
            {originalActiveTabIndex === 5 && (
              <SystemReportsTab 
                isDark={isDark}
                selectedPeriod={selectedPeriod}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MetricsDashboard;